import React, { useState, useEffect } from "react"
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { resetPasswordWithToken } from "../services/authService"
import { useNavigate } from "react-router-dom"

export default function ResetPasswordView() {
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [token, setToken] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const rawHash = window.location.hash || "";
    const hashParams = new URLSearchParams(rawHash.startsWith("#") ? rawHash.substring(1) : rawHash);
    const tokenFromHash = hashParams.get("access_token") || hashParams.get("token");
    const errorFromHash = hashParams.get("error") || hashParams.get("error_description");

    if (tokenFromHash) {
      console.debug("[reset] token detected in hash");
      setToken(tokenFromHash);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const tokenFromQuery = searchParams.get("token") || searchParams.get("access_token");
    const errorFromQuery = searchParams.get("error") || searchParams.get("error_description");

    if (tokenFromQuery) {
      console.debug("[reset] token detected in query");
      setToken(tokenFromQuery);
      return;
    }

    const finalError = errorFromHash || errorFromQuery;
    if (finalError) {
      const msg = decodeURIComponent(finalError).replace(/\+/g, " ");
      setError(msg);
      toast.error(msg);
      return;
    }

    // 4) Si no hay token ni error — enlace inválido/expirado
    toast.error("Enlace inválido o expirado.");
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError("Por favor completa ambas contraseñas.")
      return
    }

    if (newPassword.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.")
      return
    }

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.")
      return
    }

    setIsLoading(true)
    try {
      const response = await resetPasswordWithToken(token, newPassword)
      if(!response.ok){
        toast.error(response.message || "No se pudo actualizar la contraseña.")
      }
      toast.success("Contraseña actualizada exitosamente.")
      navigate("/login")
    } catch (err) {
      console.error(err)
      const msg = err?.message || "No se pudo actualizar la contraseña."
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-4xl border border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">RECUPERAR CONTRASEÑA</h1>
        <form className="space-y-6" onSubmit={handlePasswordSubmit}>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Nueva contraseña</label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Confirmar contraseña</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#00376d] hover:bg-[#0051a1] text-white font-medium py-3 px-4 rounded-lg"
          >
            {isLoading ? "Actualizando..." : "Actualizar contraseña"}
          </button>
        </form>
      </div>
    </div>
  )
}
